import { jsPDF } from "jspdf";

export default function generateZoneUsageReport(data) {

  // Extract yearStart, yearEnd, and reportData from data object
  const { yearStart, yearEnd, reportData } = data;

  const doc = new jsPDF('portrait');

  // Column definitions for consistent layout (defined early to avoid hoisting issues)
  const columns = [
    { header: "Zone Name", width: 80, key: 'zoneName' },
    { header: "Total Skiers", width: 50, key: 'totalSkiers' },
    { header: "Total Groups", width: 50, key: 'totalGroups' },
  ];

  // Function to wrap text within a column width (defined early to avoid hoisting issues)
  const wrapText = (doc, text, maxWidth) => {
    if (!text) return [''];

    const words = text.toString().split(' ');
    const lines = [];
    let currentLine = '';

    words.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const textWidth = doc.getTextWidth(testLine);

      if (textWidth <= maxWidth - 4) { // Leave 4 units padding
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          // Single word is too long, truncate it
          lines.push(word.substring(0, Math.floor(word.length * maxWidth / textWidth)));
        }
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines.length > 0 ? lines : [''];
  };

  // Function to draw text in a cell with proper boundaries (defined early to avoid hoisting issues)
  const drawCellText = (doc, text, x, y, width, height) => {
    const lines = wrapText(doc, text, width);
    const lineHeight = 3;

    // Draw each line of text
    lines.forEach((line, index) => {
      if (index * lineHeight < height - 2) { // Make sure text fits in cell height
        doc.text(line, x + 2, y + 3 + (index * lineHeight));
      }
    });

    return lines.length * lineHeight;
  };

  let yPos = 10; // Set initial Y position

  doc.setFontSize(18);
  doc.text("Zone Usage Report", 105, yPos, { align: "center" });
  yPos += 7;

  // Add date range subtitle
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100); // Gray color for subtitle
  doc.text("Date Range: " + yearStart + " to " + yearEnd, 105, yPos, { align: "center" });
  doc.setTextColor(0, 0, 0); // Reset to black
  yPos += 10;

  // Function to draw table headers using the column system
  const drawHeaders = (doc, y) => {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');

    let x = 10;
    columns.forEach(col => {
      doc.setDrawColor(0);
      doc.setFillColor(211, 211, 211);
      doc.rect(x, y, col.width, 8, 'FD');
      doc.setTextColor(0, 0, 0);
      doc.text(col.header, x + 2, y + 5.5);
      x += col.width;
    });

    doc.setFont('helvetica', 'normal');
    return y + 8;
  };

  // Function to render a zone using the column system
  const renderZone = (doc, zone, y) => {
    doc.setFontSize(10);

    // Calculate the row height needed based on text wrapping
    let maxRowHeight = 8; // Minimum row height

    // First pass: calculate required height for this row
    columns.forEach(col => {
      let cellText = '';
      if (col.key === 'zoneName') {
        cellText = zone.zoneName || 'N/A';
      } else if (col.key === 'totalSkiers') {
        cellText = zone.totalSkiers?.toString() || '0';
      } else if (col.key === 'totalGroups') {
        cellText = zone.totalGroups?.toString() || '0';
      }

      const lines = wrapText(doc, cellText, col.width);
      const requiredHeight = Math.max(8, lines.length * 3 + 2);
      maxRowHeight = Math.max(maxRowHeight, requiredHeight);
    });

    // Check if we need a new page
    if (y + maxRowHeight > 280) {
      return -1; // Signal that a new page is needed
    }

    // Draw cell borders and content
    let x = 10;
    columns.forEach(col => {
      // Draw cell border
      doc.setDrawColor(200, 200, 200); // Light gray border
      doc.setFillColor(255, 255, 255);
      doc.rect(x, y, col.width, maxRowHeight, 'FD');

      // Get cell content
      let cellText = '';
      if (col.key === 'zoneName') {
        cellText = zone.zoneName || 'N/A';
      } else if (col.key === 'totalSkiers') {
        cellText = zone.totalSkiers?.toString() || '0';
      } else if (col.key === 'totalGroups') {
        cellText = zone.totalGroups?.toString() || '0';
      }

      // Set text color
      doc.setTextColor(0, 0, 0);

      // Draw cell content
      drawCellText(doc, cellText, x, y, col.width, maxRowHeight);

      x += col.width;
    });

    return y + maxRowHeight;
  };

  yPos = drawHeaders(doc, yPos);
  yPos += 2;

  reportData.forEach(zone => {
    const newY = renderZone(doc, zone, yPos);
    if (newY === -1) {
      // Need new page
      doc.addPage();
      yPos = 10;
      doc.setFontSize(18);
      doc.text("Zone Usage Report", 105, yPos, { align: "center" });
      yPos += 7;

      // Add date range subtitle
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100); // Gray color for subtitle
      doc.text("Date Range: " + yearStart + " to " + yearEnd, 105, yPos, { align: "center" });
      doc.setTextColor(0, 0, 0); // Reset to black
      yPos += 10;

      yPos = drawHeaders(doc, yPos);
      yPos += 2;
      yPos = renderZone(doc, zone, yPos);
    } else {
      yPos = newY;
    }
  });

  //const reportDate = new Date().toISOString().split('T')[0];
  doc.save(`zone-usage-report-${yearStart}to${yearEnd}.pdf`);
}
